import { findVariable, type ModelVariable } from '../../api/models'
import { _resetRadarMapCache, radarMapForLegend, type RadarMapIndex } from './radarMapLayer'
import type { LegendRef } from './registry'
import { getLegendGraphicUrl, type WmsTarget } from './wms'

/**
 * `GetLegendGraphic&format=application/json` — the colormap the server
 * actually rendered with, entry by entry, so the bar we draw cannot drift from
 * the imagery. The `image/png` form exists but is styled light-on-transparent
 * and would need a plate behind it on this dark theme.
 *
 * Probed 2026-09-16: reflectivity returns 71 entries (roughly one per dBZ plus
 * `nodata` sentinels), NDFD temperature 38, PoP 10, GOES visible 2. Alerts
 * return Polygon rules with no colormap at all, which is a `null` legend here
 * rather than an error.
 */

export interface LegendStop {
  value: number
  color: string
  opacity: number
  label: string
}

export interface LegendSpec {
  title: string
  units: string
  stops: LegendStop[]
  /**
   * A sentence instead of a ramp, for a layer whose legend is not a gradient.
   *
   * An isobar field's legend is its *interval*, and a wind barb's is the
   * convention that decodes it — "half barb 5 kt, full 10, pennant 50". A
   * colour bar for either would be a bar with one colour on it. When this is
   * set, `stops` is empty and `Legend.svelte` prints this instead.
   */
  note?: string
  /**
   * Several bars, each named, for a layer with more than one ramp: the radar
   * map draws rain and snow in two. `stops` is then the first band's, which
   * is what the collapsed swatch and the tap menu's swatch use.
   */
  bands?: { label: string; stops: LegendStop[] }[]
}

interface ColormapEntry {
  label?: string
  quantity?: string
  color?: string
  opacity?: string
}

const cache = new Map<string, Promise<LegendSpec | null>>()

export function parseLegend(json: unknown, units: string): LegendSpec | null {
  const legend = (json as { Legend?: unknown[] })?.Legend?.[0] as
    | { title?: string; layerName?: string; rules?: unknown[] }
    | undefined
  if (!legend) return null

  const symbolizer = (legend.rules?.[0] as { symbolizers?: unknown[] } | undefined)
    ?.symbolizers?.[0] as { Raster?: { colormap?: { entries?: ColormapEntry[] } } } | undefined
  const entries = symbolizer?.Raster?.colormap?.entries
  if (!entries?.length) return null // Categorical (alerts), or no legend at all.

  const stops: LegendStop[] = []
  for (const e of entries) {
    const opacity = e.opacity === undefined ? 1 : Number(e.opacity)
    const label = (e.label ?? '').trim()
    // `nodata` and fully transparent entries are the ramp's padding, not part
    // of it; drawing them puts a white notch on the end of every bar.
    if (label.toLowerCase() === 'nodata' || opacity === 0) continue
    if (!e.color) continue
    const value = Number(e.quantity ?? NaN)
    stops.push({ value, color: e.color, opacity, label: tickLabel(label, value) })
  }
  if (stops.length < 2) return null

  return { title: legend.title ?? legend.layerName ?? '', units, stops }
}

/**
 * The number a stop prints on the bar.
 *
 * A GeoServer colormap label is normally the quantity already ("25", "0.1",
 * "70%"). NDFD apparent temperature's is not: captured 2026-09-17, twenty of
 * its thirty-two entries carry the literal string `values`, plainly a template
 * that never got filled in upstream. Printed as-is that is a colour bar
 * labelled "values values values", so a non-empty label with no digit in it
 * falls back to its own quantity.
 *
 * An *empty* label keeps its emptiness: that is how a ramp says "this stop is
 * a colour, not a tick", which is what NDFD temperature's -500 °F sentinel is.
 */
function tickLabel(label: string, value: number): string {
  if (label === '' || /\d/.test(label)) return label
  return Number.isFinite(value) ? String(value) : label
}

/**
 * A model field's ramp, straight out of the table that coloured the pixels.
 *
 * Every stop there is `(value, "#rrggbb")` by construction, so unlike the
 * GeoServer branch there is nothing to drop — but the same rules apply and are
 * written as code rather than assumed: a stop with no colour is not drawn, and
 * a label is never invented. Ours is well-formed; the code that reads it is
 * the same code that reads NDFD's, and that one is not.
 */
export function modelLegend(variable: ModelVariable): LegendSpec | null {
  const stops: LegendStop[] = []
  for (const [value, color] of variable.colormap ?? []) {
    if (typeof color !== 'string' || !/^#[0-9a-f]{6}$/i.test(color)) continue
    if (!Number.isFinite(value)) continue
    stops.push({ value, color, opacity: 1, label: String(value) })
  }
  if (stops.length < 2) return null
  return { title: variable.label, units: variable.units, stops }
}

/**
 * A contour or barb layer's legend: a sentence built from the catalog's own
 * block.
 *
 * Nothing here restates what the field is. The interval and its units come out
 * of `contour`, the speed units out of `barb`, and the barb convention is the
 * one thing this app states rather than reads — because it is a drawing
 * convention rather than a property of the data, and `barbGlyph.ts` is what
 * implements it.
 */
export function vectorLegend(
  variable: ModelVariable,
  kind: 'contour' | 'barb',
): LegendSpec | null {
  if (kind === 'contour') {
    const spec = variable.contour
    if (!spec || !Number.isFinite(spec.interval)) return null
    // The units on both numbers: "every 4 hPa, every 20 heavier" reads as
    // twenty *lines* rather than twenty hectopascals, which is the one thing
    // this sentence exists to say.
    const heavy =
      spec.emphasis > 0
        ? `, every ${formatInterval(spec.interval * spec.emphasis)} ${spec.units} heavier`
        : ''
    return {
      title: variable.label,
      units: spec.units,
      stops: [],
      note: `Lines every ${formatInterval(spec.interval)} ${spec.units}${heavy}`,
    }
  }
  const spec = variable.barb
  if (!spec) return null
  return {
    title: variable.label,
    units: spec.units,
    stops: [],
    // The convention, in the order a reader decodes a barb in.
    note: `Half barb 5 ${spec.units} · full 10 · pennant 50`,
  }
}

/**
 * The radar map's legend: a bar each for rain and snow, ticked in the "Next
 * hour" section's words at the band boundaries, out of the ramps the worker
 * wrote with the plates. No units: the bars are intensity, and the tap menu
 * gives rain's in/h.
 */
export function nowcastLegend(index: RadarMapIndex): LegendSpec | null {
  const words = new Map([
    [0, 'Light'],
    [1 / 3, 'Moderate'],
    [2 / 3, 'Heavy'],
  ])
  const band = (ramp: [number, string][]): LegendStop[] =>
    ramp
      .filter(([v, c]) => Number.isFinite(v) && /^#[0-9a-f]{6}$/i.test(c))
      .map(([value, color]) => {
        const named = [...words].find(([at]) => Math.abs(at - value) < 1e-3)
        return { value, color, opacity: 1, label: named ? named[1] : '' }
      })
  const rain = band(index.ramp.rain)
  const snow = band(index.ramp.snow)
  if (rain.length < 2 || snow.length < 2) return null
  return {
    title: 'Radar + next hour',
    units: '',
    stops: rain,
    bands: [
      { label: 'Rain', stops: rain },
      { label: 'Snow', stops: snow },
    ],
  }
}

/** `4`, `60`, `0.5` — a number a person reads, not `4.0`. */
function formatInterval(value: number): string {
  return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(2)))
}

/**
 * The colour bar for a registry row, dispatching on where it comes from.
 *
 * `legendSource` used to assume GeoServer, which held for every Phase 2 row
 * and breaks the moment we render something ourselves.
 */
export async function fetchLegendFor(ref: LegendRef): Promise<LegendSpec | null> {
  if (ref.kind === 'nowcast') {
    let pending = cache.get('nowcast')
    if (!pending) {
      pending = radarMapForLegend()
        .then((index) => (index ? nowcastLegend(index) : null))
        .catch(() => null)
      cache.set('nowcast', pending)
    }
    return pending
  }
  if (ref.kind === 'vector') {
    const key = `vector:${ref.model}:${ref.variable}:${ref.vector}`
    let pending = cache.get(key)
    if (!pending) {
      pending = findVariable(ref.model, ref.variable)
        .then((found) => (found ? vectorLegend(found.variable, ref.vector) : null))
        .catch(() => null)
      cache.set(key, pending)
    }
    return pending
  }
  if (ref.kind === 'model') {
    const key = `model:${ref.model}:${ref.variable}`
    let pending = cache.get(key)
    if (!pending) {
      pending = findVariable(ref.model, ref.variable)
        .then((found) => (found ? modelLegend(found.variable) : null))
        .catch(() => null)
      cache.set(key, pending)
    }
    return pending
  }
  return fetchLegend(ref, ref.units)
}

export async function fetchLegend(target: WmsTarget, units: string): Promise<LegendSpec | null> {
  const url = getLegendGraphicUrl(target)
  let pending = cache.get(url)
  if (!pending) {
    // Failure is silent by design: a missing colour bar is not worth
    // interrupting someone for.
    pending = fetch(url)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => (j ? parseLegend(j, units) : null))
      .catch(() => null)
    cache.set(url, pending)
  }
  return pending
}

/**
 * Which stops get a printed label. Reflectivity has ~70 entries and would
 * otherwise draw 70 overlapping numbers; a ramp that already labels sparsely
 * (`0%`, `10%` …) is left alone.
 *
 * `max` is a hard cap, including the top of the ramp. It used to be a cap plus
 * one, which is invisible when the labels are two characters wide and is an
 * extra collision when they are not.
 */
export function labelledStops(stops: readonly LegendStop[], max = 6): LegendStop[] {
  const named = stops.filter((s) => s.label !== '')
  if (named.length === 0) return []
  if (named.length <= max) return [...named]
  const stride = Math.ceil(named.length / max)
  const last = named.length - 1
  const picked: number[] = []
  for (let i = 0; i <= last; i += stride) picked.push(i)
  // Always show the top of the ramp: it is the value people look for — and
  // drop whatever sits within a stride of it first. A stride is the gap every
  // other pair of marks has, and less than that is two labels touching: the
  // 14-stop wind ramp put its last chosen mark two stops from the end and the
  // bar read "90110". Looked at, 2026-09-19.
  while (picked.length > 0 && last - picked[picked.length - 1] < stride) picked.pop()
  picked.push(last)
  return picked.map((i) => named[i])
}

/** An estimate of a 10 px tabular numeral, plus the gap two ticks need between
 *  them. Nothing here needs to be exact: the answer is a small integer, and
 *  being a tick out either way is a slightly emptier or fuller bar. */
const MARK_CHAR_PX = 5.6
const MARK_GAP_PX = 6

/**
 * How many ticks fit across a bar `widthPx` wide.
 *
 * Six ticks is right for a ramp labelled "20", "40", "60" and wrong for one
 * labelled "100.0 - 149.9": lightning strike density labels every entry with
 * its whole interval, and six of those on a 220 px plate render as
 * `0.10-75.24 0.9 50 - 9.9 25.0 - 490.0` — a solid line of overlapping digits.
 * The widest label decides, because the marks are absolutely positioned and
 * any one of them can be the collision.
 */
export function fitMarks(stops: readonly LegendStop[], widthPx: number, max = 6): number {
  const widest = stops.reduce((n, s) => Math.max(n, s.label.length), 0)
  if (widest === 0) return max
  const fits = Math.floor(widthPx / (widest * MARK_CHAR_PX + MARK_GAP_PX))
  // Two — the ends of the ramp — is the fewest that still says anything.
  return Math.max(2, Math.min(max, fits))
}

/** Tests only: the module-level legend cache outlives a component. */
export function _resetLegendCache(): void {
  cache.clear()
  _resetRadarMapCache()
}

/**
 * The colour a reading sits in, for the tap menu's swatch (Stage 10.5, 2c).
 *
 * The stop at or below the value: the ramp as its legend draws it, never an
 * interpolated colour that no stop states. A value below the ramp's first stop
 * has no colour — a sentinel or a floor, which the readout already words — and
 * neither does a stop the legend drew translucent, whose colour depends on
 * what is under it. The legend is the one `Legend.svelte` fetched, from the
 * same cache, so a swatch costs no request.
 */
export function swatchFor(spec: LegendSpec | null, value: number | null): string | null {
  if (!spec || value === null || !Number.isFinite(value)) return null
  let hit: LegendStop | null = null
  for (const stop of spec.stops) {
    if (!Number.isFinite(stop.value)) continue
    if (stop.value <= value && (hit === null || stop.value >= hit.value)) hit = stop
  }
  return hit && hit.opacity >= 1 ? hit.color : null
}
